/**
 * Accept-invite landing page.
 *
 * Therapist invited a new parent by email → parent gets a magic link
 * pointing here (?token=xxx). Parent picks their password, we call
 * `/api/auth/parent-invite/redeem` which:
 *   - hashes + persists the password on the pre-provisioned parent_self user
 *   - marks the token used
 *   - returns our JWT so the parent is logged in immediately
 *
 * We then smart-redirect based on their capabilities (they'll usually
 * have both PFP and clinical_parent since a therapist invited them and
 * the therapist_parent_link is already active).
 */
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Heart } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ParentAcceptInvite() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);

  useEffect(() => {
    if (!token || token.length < 20) {
      setTokenValid(false);
    }
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/auth/parent-invite/redeem`,
        { token, password },
      );
      const { token: jwt, user } = r.data;
      localStorage.setItem('token', jwt);
      localStorage.setItem('user', JSON.stringify(user));
      toast.success(`Welcome, ${(user.name || 'friend').split(' ')[0]}!`);
      // Refresh /auth/me to get capabilities and smart-redirect.
      const me = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      const caps = me.data?.capabilities || {};
      localStorage.setItem('user', JSON.stringify(me.data));
      window.location.href =
        caps.pfp && caps.clinical_parent
          ? '/'
          : caps.pfp
            ? '/parent-app/home'
            : '/';
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not accept invite. Try again.';
      toast.error(msg);
      if (err?.response?.status === 404 || err?.response?.status === 410) {
        setTokenValid(false);
      }
    } finally {
      setBusy(false);
    }
  };

  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
        <div className="max-w-sm text-center" data-testid="parent-invite-invalid">
          <Heart className="w-10 h-10 text-[#7C6F9C]/60 mx-auto mb-4" />
          <h1 className="text-lg font-semibold text-stone-900 mb-2">
            This invite link isn&apos;t valid
          </h1>
          <p className="text-sm text-stone-600 leading-relaxed mb-6">
            It may have expired or already been used. Ask your therapist to
            send you a fresh invite.
          </p>
          <button
            onClick={() => navigate('/parent-app/login')}
            className="text-[#7C6F9C] hover:underline text-sm font-medium"
          >
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4 py-10">
      <div className="w-full max-w-md" data-testid="parent-invite-form">
        <div className="text-center mb-6">
          <Heart className="w-9 h-9 text-[#7C6F9C] mx-auto mb-3" />
          <h1 className="text-2xl font-semibold text-stone-900">
            Welcome to PlayFirst
          </h1>
          <p className="text-sm text-stone-600 mt-2 leading-relaxed">
            Your therapist has invited you. Pick a password to get started.
          </p>
        </div>
        <form
          onSubmit={submit}
          className="rounded-3xl bg-white shadow-sm border border-stone-200 p-6 space-y-5"
        >
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1.5">
              Choose a password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              placeholder="At least 8 characters"
              className="w-full h-11 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
              data-testid="invite-password"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1.5">
              Confirm password
            </label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
              placeholder="Type it again"
              className="w-full h-11 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
              data-testid="invite-password-confirm"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            className="w-full h-11 rounded-full bg-[#7C6F9C] hover:bg-[#6a5d8b] text-white font-medium inline-flex items-center justify-center gap-2 transition-all disabled:opacity-50"
            data-testid="invite-submit"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {busy ? 'Setting up…' : 'Set password & continue'}
          </button>
        </form>
      </div>
    </div>
  );
}
